import { useEffect, useRef, useState } from "react";
import { fetchGallery, resolveUrl } from "@/lib/api";

function BeforeAfter({ before, after, alt }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const updateFromClientX = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  // Only attach window listeners while user is actively dragging — avoids
  // expensive global mousemove/touchmove handlers running during regular scroll.
  const startDrag = (e) => {
    e.preventDefault();
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(x);

    const onMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      updateFromClientX(cx);
    };
    const stop = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", stop);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", stop);
  };

  return (
    <div
      ref={ref}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl select-none cursor-ew-resize group"
    >
      <img src={resolveUrl(after)} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={resolveUrl(before)} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      {/* Slider line + handle */}
      <div className="absolute top-0 bottom-0 w-px bg-white/90 pointer-events-none" style={{ left: `${pos}%` }} />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-cyan grid place-items-center pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <span className="mono text-[10px] font-bold text-[#02121a]">⇄</span>
      </div>
      {/* Labels */}
      <span className="absolute top-3 left-3 mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full bg-black/60 text-white/80">
        Før
      </span>
      <span className="absolute top-3 right-3 mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full bg-cyan/90 text-[#02121a] font-semibold">
        Etter
      </span>
    </div>
  );
}

export default function Gallery() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchGallery().then(setItems).catch(() => setItems([]));
  }, []);

  if (!items.length) return null;

  return (
    <section id="galleri" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 sm:mb-20">
          <div className="max-w-3xl">
            <div className="mono text-[11px] uppercase tracking-[0.3em] text-cyan mb-5 reveal">
              / Før & Etter
            </div>
            <h2 className="reveal text-4xl sm:text-6xl font-bold text-white leading-[1] mb-5" data-testid="gallery-headline">
              Resultater du <span className="text-gradient">kan se.</span>
            </h2>
            <p className="reveal text-white/65 text-lg max-w-xl">
              Dra glide-kontrollen for å se forskjellen fra før til etter. Ekte
              prosjekter, ekte resultater fra våre kunder.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {items.map((g, i) => (
            <article
              key={g.id}
              className="reveal"
              style={{ transitionDelay: `${i * 100}ms` }}
              data-testid={`gallery-item-${i}`}
            >
              <BeforeAfter before={g.before_url} after={g.after_url} alt={g.title} />
              <div className="mt-5 px-1">
                <h3 className="text-xl font-bold text-white mb-1">{g.title}</h3>
                {g.description && <p className="text-white/60 text-sm">{g.description}</p>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
